import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { HeroSlide } from "@/types/media";

interface HeroCarouselProps {
  slides: HeroSlide[];
}

export default function HeroCarousel({ slides }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);

  const go = useCallback(
    (next: number) => {
      setIndex(((next % slides.length) + slides.length) % slides.length);
    },
    [slides.length]
  );

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [slides.length]);

  const active = slides[index];

  return (
    <section className="relative w-full h-[440px] md:h-[600px] overflow-hidden bg-primary-950">
      {slides.map((slide, i) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "opacity-0"
          }`}
        >
          <img
            src={slide.image}
            alt={`${slide.title} 焦点图`}
            title={`${slide.title} 影视焦点推荐`}
            className="h-full w-full object-cover object-top"
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-950/60 to-primary-950/20" />
      <div className="absolute inset-0 bg-gradient-to-t from-primary-950/80 via-transparent to-transparent" />

      <div className="relative z-10 flex h-full w-full items-center">
        <div className="w-full px-4 md:px-8">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 text-xs font-medium text-primary-950">
              <i className="ri-fire-fill" />
              {active.tag}
            </span>
            <h2 className="mt-4 text-4xl font-bold leading-tight text-background-50 md:text-6xl">
              {active.title}
            </h2>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-background-200 md:text-base">
              {active.subtitle}
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-background-300 md:text-sm">
              <span className="whitespace-nowrap">{active.meta}</span>
              <span className="flex items-center gap-1 whitespace-nowrap">
                <i className="ri-star-fill text-accent-400" />
                {active.rating.toFixed(1)} 分
              </span>
            </div>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${active.movieId}`}
                className="flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-medium text-primary-950 transition-colors hover:bg-accent-400"
              >
                <i className="ri-play-fill text-lg" />
                查看详情
              </Link>
              <Link
                to="/movies"
                className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-200/50 bg-background-50/10 px-5 py-2.5 text-sm text-background-50 backdrop-blur transition-colors hover:bg-background-50/20"
              >
                <i className="ri-compass-3-line text-lg" />
                浏览电影
              </Link>
            </div>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => go(index - 1)}
        className="absolute left-3 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/20 text-background-50 backdrop-blur transition-colors hover:bg-background-50/35 md:left-6 md:h-11 md:w-11"
        aria-label="上一张"
      >
        <i className="ri-arrow-left-s-line text-2xl" />
      </button>
      <button
        type="button"
        onClick={() => go(index + 1)}
        className="absolute right-3 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/20 text-background-50 backdrop-blur transition-colors hover:bg-background-50/35 md:right-6 md:h-11 md:w-11"
        aria-label="下一张"
      >
        <i className="ri-arrow-right-s-line text-2xl" />
      </button>

      <div className="absolute bottom-5 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2">
        {slides.map((slide, i) => (
          <button
            key={slide.id}
            type="button"
            onClick={() => go(i)}
            aria-label={`切换到第 ${i + 1} 张`}
            className={`h-1.5 rounded-full transition-all duration-300 ${
              i === index ? "w-7 bg-accent-500" : "w-3 bg-background-50/50 hover:bg-background-50/80"
            }`}
          />
        ))}
      </div>
    </section>
  );
}